import { useState, ReactNode } from 'react';

export interface SidebarItem {
  id: string;
  icon: ReactNode;
  name: string;
  description?: string;
  subItems?: { id: string; label: string }[];
  badge?: string;
}

interface SidebarLayoutProps {
  title: string;
  subtitle?: string;
  items: SidebarItem[];
  activeItemId: string;
  activeSubItemId?: string;
  onItemChange: (itemId: string) => void;
  onSubItemChange?: (subItemId: string) => void;
  children: ReactNode;
  contentHeader?: ReactNode;
}

export function SidebarLayout({
  title,
  subtitle,
  items,
  activeItemId,
  activeSubItemId,
  onItemChange,
  onSubItemChange,
  children,
  contentHeader,
}: SidebarLayoutProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);

  return (
    <div className="flex h-[calc(100vh-4rem)]">
      {/* Left Sidebar */}
      <div
        className={`
          flex-shrink-0 bg-surface-800 border-r border-surface-400/20 overflow-y-auto
          transition-all duration-200 ease-in-out
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        {/* Sidebar Header */}
        <div className="p-3 border-b border-surface-400/20 flex items-center justify-between">
          {!isCollapsed && (
            <div className="min-w-0">
              <h1 className="text-lg font-semibold text-white truncate">{title}</h1>
              {subtitle && (
                <p className="text-xs text-text-tertiary mt-0.5 truncate">{subtitle}</p>
              )}
            </div>
          )}
          <button
            onClick={() => setIsCollapsed(!isCollapsed)}
            className={`p-1.5 text-text-secondary hover:text-white hover:bg-surface-700 rounded-lg transition-colors flex-shrink-0 ${isCollapsed ? 'mx-auto' : ''}`}
            title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
          >
            <CollapseIcon isCollapsed={isCollapsed} />
          </button>
        </div>

        {/* Navigation Items */}
        <nav className="p-2">
          {items.map((item) => {
            const isActive = activeItemId === item.id;
            return (
              <div key={item.id}>
                <button
                  onClick={() => onItemChange(item.id)}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-all
                    ${isActive
                      ? 'bg-primary-500/15 text-white'
                      : 'hover:bg-surface-700 text-text-secondary hover:text-white'
                    }
                    ${isCollapsed ? 'justify-center' : ''}
                  `}
                  title={isCollapsed ? item.name : undefined}
                >
                  <span className={`flex-shrink-0 ${isActive ? 'text-primary-400' : ''}`}>
                    {item.icon}
                  </span>
                  {!isCollapsed && (
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className={`text-sm font-medium truncate ${isActive ? 'text-white' : ''}`}>
                          {item.name}
                        </span>
                        {item.badge && (
                          <span className="text-xs px-1.5 py-0.5 bg-surface-600 text-text-secondary rounded">
                            {item.badge}
                          </span>
                        )}
                      </div>
                      {item.description && (
                        <div className="text-xs text-text-tertiary truncate">{item.description}</div>
                      )}
                    </div>
                  )}
                </button>

                {/* Sub-items when item is active and not collapsed */}
                {isActive && !isCollapsed && item.subItems && onSubItemChange && (
                  <div className="ml-8 mt-1 mb-2 space-y-0.5">
                    {item.subItems.map((subItem) => (
                      <button
                        key={subItem.id}
                        onClick={() => onSubItemChange(subItem.id)}
                        className={`
                          w-full text-left px-3 py-1.5 text-sm rounded transition-colors
                          ${activeSubItemId === subItem.id
                            ? 'text-primary-400 bg-primary-500/10'
                            : 'text-text-tertiary hover:text-text-secondary'
                          }
                        `}
                      >
                        {subItem.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </div>

      {/* Main Content Area */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-6xl p-6">
          {contentHeader}
          {children}
        </div>
      </div>
    </div>
  );
}

function CollapseIcon({ isCollapsed }: { isCollapsed: boolean }) {
  return (
    <svg
      className={`w-5 h-5 transition-transform duration-200 ${isCollapsed ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M11 19l-7-7 7-7m8 14l-7-7 7-7" />
    </svg>
  );
}
